<script setup lang="ts">
import type { Feature, Point } from "geojson";
import type { PointExpression } from "leaflet";
import { storeToRefs } from "pinia";
import { useCartography } from "~/modules/cartography/composables/useCartography";
import type { FeatureCollectionWithStyle } from "~/modules/cartography/models/FeatureCollectionWithStyle.interface";
import type { GeoJsonPropertiesWithStyle } from "~/modules/cartography/models/GeoJsonPropertiesWithStyle.interface";
import { computeIconClass } from "~/modules/cartography/utils/deviceMapTools";
import { useFetchDtwinModelUIConfigs } from "~/modules/dtwin/composables/useFetchDtwinModelUIConfigs";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { getDtwinTypeLabel } from "~/modules/dtwin/utils/getDtwinTypeLabel";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import ILxcDropletDown from "~icons/lxc-custom/droplet-down";

const { currentOrganization } = storeToRefs(useOrganizationStore());
const { selectedSectors } = storeToRefs(useSectorStore());

const {
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinlModels,
} = useFetchDtwinModels({
  organizationCode: currentOrganization.value?.code,
});

const {
  error: errorAllDtwinModelUIConfigs,
  results: resultsAllDtwinModelUIConfigs,
  fetchAll: fetchAllDtwinModelUIConfigs,
} = useFetchDtwinModelUIConfigs({
  organizationCode: currentOrganization.value?.code,
});

const {
  onSearch,
  searchOnSectors,
  allResults: allDtwins,
  error: errorAllDtwins,
  fetchAllData: fetchAllDtwins,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
});

const {
  error: errorCartographySettings,
  cartographySettings,
  getCartographySettings,
} = useCartography();

// Global loading of the map, default true in order to not display the map before fetching data
const isLoading = ref(true);

const selectedDtwin: Ref<Dtwin | undefined> = ref();

const mapGeoJson: Ref<FeatureCollectionWithStyle | undefined> = ref();

/**
 * These magic numbers place the tip of the icon on the device's gps point.
 */
const iconAnchorMagicNumbers: PointExpression = [13.1, 32];

/**
 * Build and return a GeoJson feature collection from a dtwin list
 * @param dtwins
 */
const dtwinsToGeoJson: (dtwins: Dtwin[]) => FeatureCollectionWithStyle = (
  dtwins: Dtwin[],
) => {
  const dtwinsGeoJsons: Feature<Point, GeoJsonPropertiesWithStyle>[] = [];
  for (const dtwin of dtwins) {
    if (
      dtwin.features?.location?.reported?.latitude !== undefined &&
      dtwin.features?.location?.reported?.longitude !== undefined
    ) {
      const deviceModelUiConfig = (
        resultsAllDtwinModelUIConfigs.value || []
      ).find(
        (modelUiConfig) =>
          modelUiConfig.deviceModelUid === dtwin.deviceModelUid,
      );

      dtwinsGeoJsons.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            parseFloat(dtwin.features?.location?.reported?.latitude),
            parseFloat(dtwin.features?.location?.reported?.longitude),
          ],
        },
        properties: {
          icon: ILxcDropletDown,
          iconAnchor: iconAnchorMagicNumbers,
          overlayIcon: deviceModelUiConfig?.properties.svgIcon,
          class: computeIconClass(
            dtwin.operationStatuses,
            deviceModelUiConfig?.properties.svgIcon,
          ),
          onClick() {
            if (toRaw(selectedDtwin.value?.uid) !== dtwin.uid) {
              selectedDtwin.value = dtwin;
            } else {
              selectedDtwin.value = undefined;
            }
          },
        },
      });
    }
  }

  return {
    type: "FeatureCollection",
    features: dtwinsGeoJsons,
  };
};

const onClose = () => (selectedDtwin.value = undefined);

async function reload() {
  isLoading.value = true;

  // only fetch cartography settings once in page
  if (!cartographySettings.value) {
    await getCartographySettings();
  }

  // only fetch dtwin models once in page
  if (!resultsAllDtwinModels.value) {
    await fetchAllDtwinlModels();
  }

  // only fetch dtwin model UI configs once in page
  if (!resultsAllDtwinModelUIConfigs.value) {
    await fetchAllDtwinModelUIConfigs();
  }

  await fetchAllDtwins();

  mapGeoJson.value = dtwinsToGeoJson(allDtwins.value || []);

  isLoading.value = false;
}

async function executeSearch(replaceHistory?: boolean) {
  await searchOnSectors(selectedSectors.value, replaceHistory);
}

watch(selectedSectors, () => executeSearch(true));

onMounted(async () => {
  await onSearch(reload, false);
  await executeSearch(true);
});
</script>

<template>
  <container-component
    :error="
      errorCartographySettings ||
      errorAllDtwinModels ||
      errorAllDtwinModelUIConfigs ||
      errorAllDtwins
    "
    :is-loading="isLoading"
    class="w-full h-full relative"
    :px="0"
    :py="0"
  >
    <map-devices-tile
      v-if="selectedDtwin"
      class="absolute mt-3 ml-14 !z-[900]"
      :dtwin="selectedDtwin"
      :model-label="
        getDtwinTypeLabel(resultsAllDtwinModels ?? [], selectedDtwin)
      "
      @close="onClose"
    />
    <map-devices-tile-no-device
      v-if="mapGeoJson && mapGeoJson.features.length <= 0"
      class="absolute inset-x-0 m-auto top-1/2 -translate-y-1/2 !z-[900]"
    />
    <map-component
      class="w-full h-full"
      :cartography-settings="cartographySettings"
      :points="mapGeoJson"
    />
  </container-component>
</template>
<style lang="scss">
// This css property is define here because tailwind css doesn't allow to target a grandchildren
.default-icon {
  svg {
    path:nth-child(2) {
      color: white;
    }
  }
}
</style>
